import { supabase } from './supabaseClient';
import { JourneyWorkflowService } from './journeyWorkflowService';
import {
  Journey,
  JourneyStep,
  JourneySetup,
  ResetJourneyRequest,
  SkipStepRequest,
  CustomizeJourneyRequest,
  JourneyHistoryEntry,
  StepSkipJustification,
  JourneyCustomization,
  JourneyActionType,
  SkipType,
  CustomizationType,
  CustomizationLevel,
  JourneyRow,
  JourneyStepRow,
  JourneyHistoryRow,
  StepSkipJustificationRow,
  JourneyCustomizationRow,
  journeyRowToJourney,
  journeyStepRowToJourneyStep,
  journeyHistoryRowToJourneyHistoryEntry,
  stepSkipJustificationRowToStepSkipJustification,
  journeyCustomizationRowToJourneyCustomization
} from '../types/journey';

/**
 * Service for advanced journey management features
 * Handles journey resets, step skipping, customizations, and history tracking
 */
export class JourneyManagementService {

  /**
   * Reset a journey with history tracking and point preservation
   * Requirements: 7
   */
  static async resetJourneyWithHistory(request: ResetJourneyRequest): Promise<{
    newJourney: Journey;
    newSteps: JourneyStep[];
    historyEntry: JourneyHistoryEntry;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Validate journey ID
      if (!request.journeyId || typeof request.journeyId !== 'string') {
        throw new Error('Valid journey ID is required');
      }

      // Get existing journey with steps for history
      const { data: existingJourneyRow, error: journeyError } = await supabase
        .from('journeys')
        .select('*')
        .eq('id', request.journeyId)
        .eq('user_id', user.id)
        .single();

      if (journeyError || !existingJourneyRow) {
        throw new Error('Journey not found or access denied');
      }

      // Get existing steps for history
      const { data: existingStepsRows, error: stepsError } = await supabase
        .from('journey_steps')
        .select('*')
        .eq('journey_id', request.journeyId)
        .order('step_index', { ascending: true });

      if (stepsError) {
        throw new Error(`Failed to retrieve existing steps: ${stepsError.message}`);
      }

      // Create history snapshot
      const previousData = {
        journey: existingJourneyRow,
        steps: existingStepsRows || [],
        customizations: await this.getJourneyCustomizations(request.journeyId)
      };

      // Record history entry
      const { data: historyRow, error: historyError } = await supabase
        .rpc('record_journey_history', {
          p_user_id: user.id,
          p_original_journey_id: request.journeyId,
          p_action_type: 'reset' as JourneyActionType,
          p_previous_data: previousData,
          p_new_data: null,
          p_reason: request.reason || 'User requested journey reset'
        });

      if (historyError) {
        console.error('Error recording journey history:', historyError);
        // Continue anyway - history is not critical for reset functionality
      }

      // Increment reset count and mark as inactive
      const newResetCount = (existingJourneyRow.reset_count || 0) + 1;
      
      const { error: deactivateError } = await supabase
        .from('journeys')
        .update({ 
          is_active: false,
          reset_count: newResetCount
        })
        .eq('id', request.journeyId);

      if (deactivateError) {
        throw new Error(`Failed to deactivate journey: ${deactivateError.message}`);
      }

      // Determine new setup
      let newSetup: JourneySetup;
      
      if (request.newSetup) {
        newSetup = request.newSetup;
      } else {
        // Reuse existing journey setup
        newSetup = {
          stage: existingJourneyRow.stage,
          market: existingJourneyRow.market,
          evaluation: existingJourneyRow.evaluation || undefined
        };
      }

      // Generate new workflow
      const { persona, steps: generatedSteps } = JourneyWorkflowService.generateWorkflow(
        newSetup.stage,
        newSetup.market,
        newSetup.evaluation
      );

      // Determine customization level based on user experience
      const isExperienced = await this.checkIfUserIsExperienced(user.id);
      const customizationLevel: CustomizationLevel = isExperienced ? 'experienced' : 'standard';

      // Create new journey record
      const journeyData = {
        user_id: user.id,
        stage: newSetup.stage,
        market: newSetup.market,
        persona,
        evaluation: newSetup.evaluation || null,
        current_step_index: 0,
        is_active: true,
        reset_count: 0, // New journey starts with 0 resets
        is_customized: false,
        customization_level: customizationLevel
      };

      const { data: newJourneyRow, error: newJourneyError } = await supabase
        .from('journeys')
        .insert(journeyData)
        .select()
        .single();

      if (newJourneyError || !newJourneyRow) {
        throw new Error(`Failed to create new journey: ${newJourneyError?.message || 'Unknown error'}`);
      }

      // Create journey steps
      const stepsData = generatedSteps.map(step => ({
        journey_id: newJourneyRow.id,
        step_index: step.stepIndex,
        title: step.title,
        title_zh: step.title_zh || null,
        description: step.description,
        description_zh: step.description_zh || null,
        actions: step.actions,
        dod_criteria: step.dodCriteria,
        time_estimate_weeks: step.timeEstimateWeeks,
        status: step.status,
        is_customized: false,
        original_step_index: step.stepIndex
      }));

      const { data: newStepsRows, error: newStepsError } = await supabase
        .from('journey_steps')
        .insert(stepsData)
        .select();

      if (newStepsError || !newStepsRows) {
        // Rollback journey creation
        await supabase.from('journeys').delete().eq('id', newJourneyRow.id);
        throw new Error(`Failed to create journey steps: ${newStepsError?.message || 'Unknown error'}`);
      }

      // Apply preserved customizations if requested
      if (request.preserveCustomizations && previousData.customizations.length > 0) {
        await this.applyPreservedCustomizations(newJourneyRow.id, previousData.customizations);
      }

      // Convert to TypeScript interfaces
      const newJourney = journeyRowToJourney(newJourneyRow as JourneyRow);
      const newSteps = newStepsRows.map(row => journeyStepRowToJourneyStep(row as JourneyStepRow));
      
      // Create history entry object (may be null if recording failed)
      const historyEntry: JourneyHistoryEntry = {
        id: historyRow || 'unknown',
        originalJourneyId: request.journeyId,
        actionType: 'reset',
        previousData,
        reason: request.reason || 'User requested journey reset',
        createdAt: new Date()
      };

      return {
        newJourney,
        newSteps,
        historyEntry
      };

    } catch (error: any) {
      console.error('Error resetting journey with history:', error);
      throw new Error(error.message || 'Failed to reset journey');
    }
  }

  /**
   * Skip a step with justification
   * Requirements: 7
   */
  static async skipStepWithJustification(request: SkipStepRequest): Promise<{
    step: JourneyStep;
    justification: StepSkipJustification;
    nextStepUnlocked?: boolean;
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Validate request
      if (!request.stepId || typeof request.stepId !== 'string') {
        throw new Error('Valid step ID is required');
      }

      if (!request.justification || request.justification.trim().length === 0) {
        throw new Error('Justification is required for skipping steps');
      }

      // Get step and verify ownership
      const { data: stepRow, error: stepError } = await supabase
        .from('journey_steps')
        .select(`
          *,
          journeys!inner(user_id, current_step_index, is_active, id)
        `)
        .eq('id', request.stepId)
        .single();

      if (stepError || !stepRow) {
        throw new Error('Step not found');
      }

      // Type assertion for the joined data
      const stepWithJourney = stepRow as JourneyStepRow & {
        journeys: { user_id: string; current_step_index: number; is_active: boolean; id: string };
      };

      // Verify user ownership and journey is active
      if (stepWithJourney.journeys.user_id !== user.id) {
        throw new Error('Access denied');
      }

      if (!stepWithJourney.journeys.is_active) {
        throw new Error('Cannot skip steps in inactive journey');
      }

      // Verify step can be skipped (must be current step or unlocked)
      if (stepWithJourney.status === 'locked') {
        throw new Error('Cannot skip locked step');
      }

      if (stepWithJourney.status === 'completed' || stepWithJourney.status === 'skipped') {
        throw new Error('Step is already completed or skipped');
      }

      // Record skip justification
      const justificationData = {
        step_id: request.stepId,
        user_id: user.id,
        justification: request.justification.trim(),
        skip_type: request.skipType
      };

      const { data: justificationRow, error: justificationError } = await supabase
        .from('step_skip_justifications')
        .insert(justificationData)
        .select()
        .single();

      if (justificationError || !justificationRow) {
        throw new Error(`Failed to record skip justification: ${justificationError?.message || 'Unknown error'}`);
      }

      // Update step status
      const { data: updatedStepRow, error: updateError } = await supabase
        .from('journey_steps')
        .update({
          status: 'skipped',
          skip_justification: request.justification.trim(),
          completed_at: new Date().toISOString()
        })
        .eq('id', request.stepId)
        .select()
        .single();

      if (updateError || !updatedStepRow) {
        throw new Error(`Failed to update step: ${updateError?.message || 'Unknown error'}`);
      }

      // Record step completion for tracking
      const completionData = {
        step_id: request.stepId,
        user_id: user.id,
        completion_method: 'skipped' as const,
        reflection_notes: request.justification.trim(),
        points_awarded: 0
      };

      const { error: completionError } = await supabase
        .from('step_completions')
        .insert(completionData);

      if (completionError) {
        console.error('Error recording step completion:', completionError);
        // Continue anyway - this is not critical
      }

      // Record history entry
      const previousStepData = stepWithJourney;
      const { error: historyError } = await supabase
        .rpc('record_journey_history', {
          p_user_id: user.id,
          p_original_journey_id: stepWithJourney.journeys.id,
          p_action_type: 'skip_step' as JourneyActionType,
          p_previous_data: { step: previousStepData },
          p_new_data: { step: updatedStepRow, justification: justificationRow },
          p_reason: `Step skipped: ${request.justification.trim()}`
        });

      if (historyError) {
        console.error('Error recording skip history:', historyError);
        // Continue anyway - history is not critical
      }

      // Check if next step should be unlocked
      let nextStepUnlocked = false;
      const nextStepIndex = stepWithJourney.step_index + 1;
      
      // Get next step
      const { data: nextStepRow, error: nextStepError } = await supabase
        .from('journey_steps')
        .select('id, status')
        .eq('journey_id', stepWithJourney.journey_id)
        .eq('step_index', nextStepIndex)
        .single();

      if (!nextStepError && nextStepRow && nextStepRow.status === 'locked') {
        // Unlock next step
        const { error: unlockError } = await supabase
          .from('journey_steps')
          .update({ status: 'current' })
          .eq('id', nextStepRow.id);

        if (!unlockError) {
          nextStepUnlocked = true;
          
          // Update journey's current step index
          const { error: journeyUpdateError } = await supabase
            .from('journeys')
            .update({ current_step_index: nextStepIndex })
            .eq('id', stepWithJourney.journey_id);

          if (journeyUpdateError) {
            console.error('Error updating journey current step:', journeyUpdateError);
          }
        }
      }

      // Convert to TypeScript interfaces
      const updatedStep = journeyStepRowToJourneyStep(updatedStepRow as JourneyStepRow);
      const justification = stepSkipJustificationRowToStepSkipJustification(justificationRow as StepSkipJustificationRow);

      return {
        step: updatedStep,
        justification,
        nextStepUnlocked
      };

    } catch (error: any) {
      console.error('Error skipping step with justification:', error);
      throw new Error(error.message || 'Failed to skip step');
    }
  }

  /**
   * Get user's journey history
   * Requirements: 7
   */
  static async getUserJourneyHistory(userId?: string, limit: number = 10): Promise<JourneyHistoryEntry[]> {
    try {
      // Get current user if not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          throw new Error('Authentication required');
        }
        targetUserId = user.id;
      }

      // Get history using the database function
      const { data: historyRows, error: historyError } = await supabase
        .rpc('get_user_journey_history', {
          p_user_id: targetUserId,
          p_limit: limit
        });

      if (historyError) {
        console.error('Database error in getUserJourneyHistory:', historyError);
        throw new Error(`Failed to retrieve journey history: ${historyError.message}`);
      }

      // Ensure historyRows is an array and handle null/undefined cases
      if (!historyRows) {
        console.log('No history rows returned, returning empty array');
        return [];
      }

      if (!Array.isArray(historyRows)) {
        console.warn('History rows is not an array:', historyRows);
        return [];
      }

      // Convert to TypeScript interfaces
      try {
        return historyRows.map((row: JourneyHistoryRow) => 
          journeyHistoryRowToJourneyHistoryEntry(row)
        );
      } catch (conversionError: any) {
        console.error('Error converting history rows:', conversionError, historyRows);
        throw new Error('Failed to process journey history data');
      }

    } catch (error: any) {
      console.error('Error getting user journey history:', error);
      throw new Error(error.message || 'Failed to retrieve journey history');
    }
  }

  /**
   * Check if user is experienced and eligible for customizations
   * Requirements: 7
   */
  static async checkIfUserIsExperienced(userId?: string): Promise<boolean> {
    try {
      // Get current user if not provided
      let targetUserId = userId;
      if (!targetUserId) {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          throw new Error('Authentication required');
        }
        targetUserId = user.id;
      }

      // Use the database function to check experience
      const { data: isExperienced, error: checkError } = await supabase
        .rpc('is_experienced_user', {
          p_user_id: targetUserId
        });

      if (checkError) {
        console.error('Error checking user experience:', checkError);
        return false; // Default to not experienced on error
      }

      return isExperienced || false;

    } catch (error: any) {
      console.error('Error checking if user is experienced:', error);
      return false; // Default to not experienced on error
    }
  }

  /**
   * Get journey customizations
   * Requirements: 7
   */
  static async getJourneyCustomizations(journeyId: string): Promise<JourneyCustomization[]> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Get customizations
      const { data: customizationRows, error: customizationError } = await supabase
        .from('journey_customizations')
        .select('*')
        .eq('journey_id', journeyId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('applied_at', { ascending: false });

      if (customizationError) {
        throw new Error(`Failed to retrieve customizations: ${customizationError.message}`);
      }

      // Convert to TypeScript interfaces
      return (customizationRows || []).map(row => 
        journeyCustomizationRowToJourneyCustomization(row as JourneyCustomizationRow)
      );

    } catch (error: any) {
      console.error('Error getting journey customizations:', error);
      throw new Error(error.message || 'Failed to retrieve customizations');
    }
  }

  /**
   * Apply journey customizations (for experienced users)
   * Requirements: 7
   */
  static async applyJourneyCustomizations(request: CustomizeJourneyRequest): Promise<{
    journey: Journey;
    steps: JourneyStep[];
    customizations: JourneyCustomization[];
  }> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Check if user is experienced
      const isExperienced = await this.checkIfUserIsExperienced(user.id);
      if (!isExperienced) {
        throw new Error('Customization features are only available for experienced users');
      }

      // Validate journey ownership
      const { data: journeyRow, error: journeyError } = await supabase
        .from('journeys')
        .select('*')
        .eq('id', request.journeyId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (journeyError || !journeyRow) {
        throw new Error('Journey not found or access denied');
      }

      // Apply each customization
      const appliedCustomizations: JourneyCustomization[] = [];
      
      for (const customization of request.customizations) {
        try {
          const applied = await this.applySingleCustomization(
            request.journeyId,
            user.id,
            customization
          );
          appliedCustomizations.push(applied);
        } catch (customError) {
          console.error('Error applying customization:', customError);
          // Continue with other customizations
        }
      }

      // Mark journey as customized
      const { error: updateError } = await supabase
        .from('journeys')
        .update({ 
          is_customized: true,
          customization_level: 'experienced'
        })
        .eq('id', request.journeyId);

      if (updateError) {
        console.error('Error marking journey as customized:', updateError);
        // Continue anyway
      }

      // Get updated journey and steps
      const { data: updatedJourneyRow, error: updatedJourneyError } = await supabase
        .from('journeys')
        .select('*')
        .eq('id', request.journeyId)
        .single();

      const { data: updatedStepsRows, error: updatedStepsError } = await supabase
        .from('journey_steps')
        .select('*')
        .eq('journey_id', request.journeyId)
        .order('step_index', { ascending: true });

      if (updatedJourneyError || !updatedJourneyRow || updatedStepsError) {
        throw new Error('Failed to retrieve updated journey data');
      }

      // Convert to TypeScript interfaces
      const journey = journeyRowToJourney(updatedJourneyRow as JourneyRow);
      const steps = (updatedStepsRows || []).map(row => 
        journeyStepRowToJourneyStep(row as JourneyStepRow)
      );

      return {
        journey,
        steps,
        customizations: appliedCustomizations
      };

    } catch (error: any) {
      console.error('Error applying journey customizations:', error);
      throw new Error(error.message || 'Failed to apply customizations');
    }
  }

  /**
   * Get step skip justifications for a journey
   * Requirements: 7
   */
  static async getStepSkipJustifications(journeyId: string): Promise<StepSkipJustification[]> {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Authentication required');
      }

      // Get skip justifications for all steps in the journey
      const { data: justificationRows, error: justificationError } = await supabase
        .from('step_skip_justifications')
        .select(`
          *,
          journey_steps!inner(journey_id)
        `)
        .eq('user_id', user.id)
        .eq('journey_steps.journey_id', journeyId)
        .order('created_at', { ascending: false });

      if (justificationError) {
        throw new Error(`Failed to retrieve skip justifications: ${justificationError.message}`);
      }

      // Convert to TypeScript interfaces
      return (justificationRows || []).map(row => 
        stepSkipJustificationRowToStepSkipJustification(row as StepSkipJustificationRow)
      );

    } catch (error: any) {
      console.error('Error getting step skip justifications:', error);
      throw new Error(error.message || 'Failed to retrieve skip justifications');
    }
  }

  /**
   * Private helper methods
   */

  /**
   * Apply preserved customizations to a new journey
   */
  private static async applyPreservedCustomizations(
    newJourneyId: string,
    customizations: JourneyCustomization[]
  ): Promise<void> {
    for (const customization of customizations) {
      try {
        // Create new customization record for the new journey
        const customizationData = {
          journey_id: newJourneyId,
          user_id: customization.userId,
          customization_type: customization.customizationType,
          original_value: customization.originalValue,
          custom_value: customization.customValue,
          is_active: true
        };

        await supabase
          .from('journey_customizations')
          .insert(customizationData);

      } catch (error) {
        console.error('Error preserving customization:', error);
        // Continue with other customizations
      }
    }
  }

  /**
   * Apply a single customization
   */
  private static async applySingleCustomization(
    journeyId: string,
    userId: string,
    customization: Partial<JourneyCustomization>
  ): Promise<JourneyCustomization> {
    // Record the customization
    const customizationData = {
      journey_id: journeyId,
      user_id: userId,
      customization_type: customization.customizationType!,
      original_value: customization.originalValue!,
      custom_value: customization.customValue!,
      is_active: true
    };

    const { data: customizationRow, error: customizationError } = await supabase
      .from('journey_customizations')
      .insert(customizationData)
      .select()
      .single();

    if (customizationError || !customizationRow) {
      throw new Error(`Failed to record customization: ${customizationError?.message || 'Unknown error'}`);
    }

    // Apply the actual customization based on type
    switch (customization.customizationType) {
      case 'step_order':
        await this.applyStepOrderCustomization(journeyId, customization.customValue);
        break;
      case 'step_modification':
        await this.applyStepModificationCustomization(journeyId, customization.customValue);
        break;
      case 'template_override':
        await this.applyTemplateOverrideCustomization(journeyId, customization.customValue);
        break;
    }

    return journeyCustomizationRowToJourneyCustomization(customizationRow as JourneyCustomizationRow);
  }

  /**
   * Apply step order customization
   */
  private static async applyStepOrderCustomization(journeyId: string, newOrder: any): Promise<void> {
    // Implementation for reordering steps
    // This would update step_index values based on the new order
    console.log('Applying step order customization:', { journeyId, newOrder });
    // TODO: Implement step reordering logic
  }

  /**
   * Apply step modification customization
   */
  private static async applyStepModificationCustomization(journeyId: string, modifications: any): Promise<void> {
    // Implementation for modifying step content
    console.log('Applying step modification customization:', { journeyId, modifications });
    // TODO: Implement step modification logic
  }

  /**
   * Apply template override customization
   */
  private static async applyTemplateOverrideCustomization(journeyId: string, overrides: any): Promise<void> {
    // Implementation for overriding templates
    console.log('Applying template override customization:', { journeyId, overrides });
    // TODO: Implement template override logic
  }
}